import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {getHasPreventPinTeamTags} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipIconWithHover} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipIconWithHover';
import {center, flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {
  CellsHeaderFiltersProps,
  Filter,
  renderFilterIcon,
  renderFilterName,
} from '../../../cells/header/cellsHeaderFilters';

/*
 * Props.
 */

interface InboxesSearchLocationDropdownProps {
  popoverProps: PopoverTransferProps;
  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  selectedFilter: InboxesSearchLocationsEnum;
  onSearchFilterSelect: (arg: InboxesSearchLocationsEnum) => void;
}

/*
 * Styles.
 */

const StyledDropdownRowDiv = styled.div`
  ${flexCenter}
`;

const StyledIconWrapperDiv = styled.div`
  grid-area: left-icon;

  width: 16px;
  margin-right: 8px;

  ${center()}
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const InboxesSearchLocationDropdown: FC<InboxesSearchLocationDropdownProps> = ({
  searchFilters,
  selectedFilter,
  onSearchFilterSelect,
  popoverProps,
}) => {
  const hasPreventPinTeamTags = useAppSelector(getHasPreventPinTeamTags);

  const {localSearchFilterName, localSearchFilterIcon, filters} = searchFilters;

  return (
    <SimpleDropdown {...popoverProps}>
      {filters.map((filter) => (
        <DropdownItem
          key={filter}
          isChecked={selectedFilter === filter}
          onSelect={() => onSearchFilterSelect(filter)}
          shouldReserveRightIcon
        >
          {localSearchFilterIcon && (
            <StyledIconWrapperDiv>{renderFilterIcon(filter, localSearchFilterIcon)}</StyledIconWrapperDiv>
          )}
          <DropdownItemContent isChecked={selectedFilter === filter}>
            <StyledDropdownRowDiv>
              {renderFilterName(filter, localSearchFilterName)}
              {maybeRenderTooltip(filter, hasPreventPinTeamTags)}
            </StyledDropdownRowDiv>
          </DropdownItemContent>
        </DropdownItem>
      ))}
    </SimpleDropdown>
  );
};

/*
 * Helpers.
 */

function maybeRenderTooltip(filter: InboxesSearchLocationsEnum, hasPreventPinTeamTags: boolean) {
  const tooltip = hasPreventPinTeamTags ? filterTooltipsPreventPinTeamTags[filter] : filterTooltips[filter];

  if (!tooltip) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipCoordinator render={() => tooltip}>
          <TooltipIconWithHover color={theme.greys.shade70} />
        </StyledTooltipCoordinator>
      )}
    </UserThemeConsumer>
  );
}

type FilterTooltips = {[T in Filter]?: ReactNode};
const filterTooltips: FilterTooltips = {
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: (
    <Trans id="search-filter-sidebar-tooltip">
      Show me results from inboxes, tags, views, and teammates that I&apos;ve added to my sidebar.
    </Trans>
  ),
  [InboxesSearchLocationsEnum.GLOBAL]: (
    <Trans id="search-filter-all-inboxes-tooltip">
      Show me search results from all inboxes, tags, views, and teammates that I have access to.
    </Trans>
  ),
};

type FilterTooltipsPreventPinTeamTags = {[T in Filter]?: ReactNode};
const filterTooltipsPreventPinTeamTags: FilterTooltipsPreventPinTeamTags = {
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: (
    <Trans id="search-filter-sidebar-tooltip-prevent-pin-team-tags">
      Show me results from inboxes, private tags, and views that I&apos;ve added to my sidebar.
    </Trans>
  ),
  [InboxesSearchLocationsEnum.GLOBAL]: (
    <Trans id="search-filter-all-inboxes-tooltip-prevent-pin-team-tags">
      Show me search results from all inboxes, private tags, and views that I have access to.
    </Trans>
  ),
};
